/**
 * Theme helpers. Mirrors the FOUC-prevention inline script in index.html —
 * any change here must also be reflected in the <head> snippet or the first
 * paint will flash the wrong palette.
 */
const STORAGE_KEY = 'ww-theme';
/**
 * Reads the persisted theme choice from localStorage, defaulting to `auto`
 * if nothing was stored (or storage is unavailable, e.g. SSR).
 */
export function getStoredTheme() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === 'light' || raw === 'dark' || raw === 'auto')
            return raw;
    }
    catch {
        /* ignore */
    }
    return 'auto';
}
/** Resolves `auto` to the current system preference. */
export function resolveEffectiveTheme(theme) {
    if (theme === 'light' || theme === 'dark')
        return theme;
    if (typeof window === 'undefined')
        return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/**
 * Persists the user's choice and updates the `theme-*` class on <html>.
 * Safe to call during render; no-ops during SSR.
 */
export function applyTheme(theme) {
    if (typeof document === 'undefined')
        return;
    try {
        localStorage.setItem(STORAGE_KEY, theme);
    }
    catch {
        /* ignore */
    }
    const effective = resolveEffectiveTheme(theme);
    const root = document.documentElement;
    if (root.classList.contains(`theme-${effective}`))
        return; // already applied
    // QA Raji #15 — theme-switch freeze. Toggling the palette changes ~100 CSS
    // custom properties; because nearly every surface has a colour `transition`
    // AND the UI is full of `backdrop-filter` (glass) layers, the browser then
    // animates all of them for the transition duration, re-compositing every
    // blurred surface each frame — a multi-second main-thread freeze on some
    // machines (and the page jumps to the top on recovery). Suppress all
    // transitions/animations for the swap so it lands in a single paint.
    document.querySelectorAll('style[data-theme-swap]').forEach((n) => n.remove());
    const killAnim = document.createElement('style');
    killAnim.setAttribute('data-theme-swap', '');
    killAnim.appendChild(document.createTextNode('*,*::before,*::after{transition:none!important;animation:none!important}'));
    document.head.appendChild(killAnim);
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(`theme-${effective}`);
    // Force the class change to commit while transitions are disabled, then
    // restore them a couple of frames later.
    void root.offsetHeight;
    const restore = () => killAnim.remove();
    requestAnimationFrame(() => requestAnimationFrame(restore));
    setTimeout(restore, 200);
}
/**
 * Subscribes to OS-level colour-scheme changes. Returns an unsubscribe fn.
 */
export function watchSystemTheme(callback) {
    if (typeof window === 'undefined')
        return () => { };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (e) => callback(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
}
